import { useCallback, useEffect, useRef, useState } from 'react';

import Tooltip from '@cartwhl/ui/atoms/Tooltip';
import Text, { type TextVariant } from '@cartwhl/ui/base/Text';

import { getChainCssColor } from './chain';

interface ChainPillProps {
  chainName: string;
  className?: string;
  textVariant?: TextVariant;
  heightPx?: number;
}

export default function ChainPill({
  chainName,
  className,
  textVariant = 'studio/body/medium/xs',
  heightPx,
}: ChainPillProps) {
  const pillRef = useRef<HTMLSpanElement>(null);
  const [showText, setShowText] = useState(true);

  const updateTextVisibility = useCallback(() => {
    const pill = pillRef.current;
    if (!pill) return;

    const styles = window.getComputedStyle(pill);
    const paddingLeft = parseFloat(styles.paddingLeft) || 0;
    const paddingRight = parseFloat(styles.paddingRight) || 0;
    const minTextWidth = estimateTwoCharacterWidthPx(pill);
    const minRequiredWidth = Math.ceil(minTextWidth + paddingLeft + paddingRight);
    const nextShowText = pill.clientWidth >= minRequiredWidth;

    setShowText((prev) => (prev === nextShowText ? prev : nextShowText));
  }, []);

  useEffect(() => {
    const pill = pillRef.current;
    if (!pill) return;

    const observer = new ResizeObserver(updateTextVisibility);
    observer.observe(pill);
    updateTextVisibility();
    return () => observer.disconnect();
  }, [updateTextVisibility]);

  return (
    <Tooltip text={`Chain ${chainName}`} side="top" inModal>
      <span
        ref={pillRef}
        className={`w-full rounded-full px-1.5 text-white ${
          heightPx === undefined ? 'block py-px leading-tight' : 'flex items-center leading-none'
        } ${
          className ?? ''
        }`}
        style={{
          backgroundColor: getChainCssColor(chainName),
          height: heightPx === undefined ? undefined : `${heightPx}px`,
        }}
      >
        {showText ? (
          <Text
            as="span"
            variant={textVariant}
            className="block truncate text-white"
          >
            {chainName}
          </Text>
        ) : (
          <span className="block h-4" aria-hidden />
        )}
      </span>
    </Tooltip>
  );
}

function estimateTwoCharacterWidthPx(element: HTMLElement): number {
  const styles = window.getComputedStyle(element);
  const font = `${styles.fontStyle} ${styles.fontWeight} ${styles.fontSize} ${styles.fontFamily}`;
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  if (!context) return 20;
  context.font = font;
  return context.measureText('WW').width;
}
